import React, { useState, useRef, useEffect } from 'react';
import { postAiQuery, postAiAnalyze, exportAiQueryCSV, getAiContext } from '../../api/internal/usersInternalApi';
import {
    AiContainer, AiTitle, AiSubtitle, AiCard,
    AiForm, AiInput, AiButton,
    AiResultMeta, AiBadge, AiDescription, AiError,
    AiTableWrapper, AiTable, AiEmpty, AiHistory, AiHistoryChip,
    AiQueryBlock, AiQueryToggle, AiQueryCode, AiExportButton, AiInsight,
    AiScopeBanner,
} from './ai.styled';

const SUGGESTIONS = [
    'Liste les collaborateurs de <ville>',
    'Liste les collaborateurs managés par <prénom nom ou mail>',
    'Appareils Windows non conformes avec leur utilisateur',
    'Comptes désactivés ayant encore une licence active',
    'Ordinateurs portables Snipe-IT dont la garantie expire dans les 6 prochains mois',
    'Qui a accès à l\'application <nom de l\'application>',
];

// Champs à afficher en priorité (dans cet ordre)
const PRIORITY_FIELDS = [
    'displayName', 'name', 'mail', 'userPrincipalName', 'givenName', 'surname',
    'employeeId', 'department', 'companyName', 'jobTitle', 'accountEnabled',
    'manager', 'licenses', 'mailboxType', 'signInActivity',
    'operatingSystem', 'operatingSystemVersion', 'model', 'serialNumber',
    'isCompliant', 'isManaged', 'isRooted', 'isDeleted', 'enrollmentType',
    'assetTag', 'category', 'statusLabel', 'manufacturer', 'company', 'location',
    'assignedTo', 'purchaseCost', 'purchaseDate', 'warrantyExpires', 'lastCheckout',
    'groupTypes', 'resourceProvisioningOptions',
    'resourceDisplayName', 'principalDisplayName', 'principalType', 'createdDateTime',
    'snipeit_category', 'snipeit_model', 'snipeit_manufacturer', 'snipeit_statusLabel',
    'snipeit_company', 'snipeit_location', 'snipeit_assetTag',
    'snipeit_purchaseCost', 'snipeit_purchaseDate', 'snipeit_warrantyExpires',
];

// Champs techniques/ID à reléguer en fin de tableau
const ID_FIELD_PATTERN = /^(objectId|deviceId|userId|groupsId|devicesId|groupsOwnerId|groupsModeratorId|informationsId|snipeitId|snipeitAssets|principalId|resourceId|appRoleId)$|AAD|Intune|physicalIds|bitlockerKeys|lapsKeys|wiFiMac|ethernetMac|__v/i;

function isIdField(key) {
    const last = key.split('.').pop();
    return ID_FIELD_PATTERN.test(last) || ID_FIELD_PATTERN.test(key);
}

function sortKeys(keys) {
    const getPriority = (k) => {
        const base = k.split('.')[0];
        const i = PRIORITY_FIELDS.indexOf(k);
        const j = PRIORITY_FIELDS.indexOf(base);
        return i !== -1 ? i : j !== -1 ? j + 0.5 : -1;
    };
    const priority = keys.filter(k => getPriority(k) !== -1 && !isIdField(k));
    const ids      = keys.filter(k => isIdField(k));
    const rest     = keys.filter(k => getPriority(k) === -1 && !isIdField(k));
    priority.sort((a, b) => getPriority(a) - getPriority(b));
    return [...priority, ...rest, ...ids];
}

function flattenObject(obj, prefix = '') {
    if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) return { [prefix]: obj };
    return Object.entries(obj).reduce((acc, [k, v]) => {
        const key = prefix ? `${prefix}.${k}` : k;
        if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
            Object.assign(acc, flattenObject(v, key));
        } else {
            acc[key] = Array.isArray(v) ? v.join(', ') : v;
        }
        return acc;
    }, {});
}

// Tronque les ObjectId (24 hex) illisibles — la valeur complète reste dans le title
function formatCell(value) {
    if (value === null || value === undefined) return '—';
    const s = String(value);
    if (/^[0-9a-f]{24}$/i.test(s)) return s.slice(0, 8) + '…';
    return s;
}

function ResultTable({ data }) {
    if (!data || data.length === 0) return <AiEmpty>Aucun résultat</AiEmpty>;

    const flatData = data.map(row => flattenObject(row));
    const SKIP = new Set(['__v', '_id']);
    const allKeys = sortKeys(
        [...new Set(flatData.flatMap(Object.keys))].filter(k => !SKIP.has(k))
    );
    const MAX_COLS = 20;
    const keys = allKeys.slice(0, MAX_COLS);

    return (
        <AiTableWrapper>
            <AiTable>
                <thead>
                    <tr>{keys.map(k => <th key={k}>{k}</th>)}</tr>
                </thead>
                <tbody>
                    {flatData.map((row, i) => (
                        <tr key={i}>
                            {keys.map(k => (
                                <td key={k} title={String(row[k] ?? '')}>
                                    {formatCell(row[k])}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </AiTable>
        </AiTableWrapper>
    );
}

export default function AiQuery() {
    const [question, setQuestion] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [showQuery, setShowQuery] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [analysis, setAnalysis] = useState(null);
    const [analyzing, setAnalyzing] = useState(false);
    const [aiContext, setAiContext] = useState(null);
    const abortRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        getAiContext().then(ctx => { if (ctx) setAiContext(ctx); });
    }, []);

    const handleSubmit = async (e, q) => {
        if (e) e.preventDefault();
        const text = (q ?? question).trim();
        if (!text) return;

        if (abortRef.current) abortRef.current.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;

        setLoading(true);
        setError(null);
        setResult(null);
        setAnalysis(null);

        const data = await postAiQuery(text, ctrl.signal);
        setLoading(false);

        if (!data) {
            setError('Erreur lors de la requête.');
            return;
        }
        if (data.error) {
            setError(data.error);
            return;
        }
        setResult(data);

        // Analyse asynchrone — ne bloque pas l'affichage du tableau
        setAnalyzing(true);
        postAiAnalyze({
            description: data.description,
            collection: data.collection,
            count: data.count,
            hasMore: data.hasMore,
            data: data.data,
        }).then(res => {
            if (res?.analysis) setAnalysis(res.analysis);
        }).finally(() => setAnalyzing(false));
    };

    const handleSuggestion = (s) => {
        setQuestion(s);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    return (
        <AiContainer>
            <AiTitle>Requête IA</AiTitle>
            <AiSubtitle>Interrogez la base de données en langage naturel — la requête MongoDB est générée automatiquement.</AiSubtitle>

            {aiContext?.role === 'rssi' && aiContext.etoiles?.length > 0 && (
                <AiScopeBanner>
                    <i className="bx bx-shield-quarter" />
                    Portée : {aiContext.etoiles.join(', ')}
                </AiScopeBanner>
            )}

            <AiCard>
                <AiHistory>
                    {SUGGESTIONS.map(s => (
                        <AiHistoryChip key={s} type="button" onClick={() => handleSuggestion(s)}>
                            {s}
                        </AiHistoryChip>
                    ))}
                </AiHistory>

                <AiForm onSubmit={handleSubmit}>
                    <AiInput
                        ref={inputRef}
                        value={question}
                        onChange={e => setQuestion(e.target.value)}
                        placeholder="Ex : Utilisateurs sans manager dans Easyteam"
                        disabled={loading}
                    />
                    <AiButton type="submit" disabled={loading || !question.trim()}>
                        {loading ? (
                            <><i className="bx bx-loader-alt bx-spin" /> Requête...</>
                        ) : (
                            <><i className="bx bx-search" /> Envoyer</>
                        )}
                    </AiButton>
                </AiForm>

                {error && <AiError>{error}</AiError>}
            </AiCard>

            {result && (
                <AiCard>
                    <AiResultMeta>
                        <AiDescription>{result.description}</AiDescription>
                        <AiBadge>{result.collection}</AiBadge>
                        <AiBadge>{result.operation}</AiBadge>
                        <AiBadge>
                            {result.hasMore ? `${result.count}+ résultats` : `${result.count} résultat${result.count !== 1 ? 's' : ''}`}
                        </AiBadge>
                        <AiExportButton
                            title="Exporter en CSV (données complètes)"
                            disabled={exporting}
                            onClick={async () => {
                                setExporting(true);
                                try {
                                    await exportAiQueryCSV({
                                        collection: result.collection,
                                        operation: result.operation,
                                        query: result.query,
                                        outputCollection: result.outputCollection,
                                    });
                                } finally { setExporting(false); }
                            }}
                        >
                            <i className={exporting ? 'bx bx-loader-alt bx-spin' : 'bx bxs-download'} />
                        </AiExportButton>
                    </AiResultMeta>

                    {analyzing && (
                        <AiInsight>
                            <i className="bx bx-loader-alt bx-spin" />
                            Analyse en cours...
                        </AiInsight>
                    )}
                    {analysis && !analyzing && (
                        <AiInsight>
                            <i className="bx bx-bulb" />
                            {analysis}
                        </AiInsight>
                    )}

                    <AiQueryBlock>
                        <AiQueryToggle type="button" onClick={() => setShowQuery(v => !v)}>
                            {showQuery ? '▾' : '▸'} Requête MongoDB générée
                        </AiQueryToggle>
                        {showQuery && (
                            <AiQueryCode>{JSON.stringify(result.query, null, 2)}</AiQueryCode>
                        )}
                    </AiQueryBlock>

                    <ResultTable data={result.data} />
                </AiCard>
            )}
        </AiContainer>
    );
}
